<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DEmployeesFilter,
  DLoading,
  DToggleField,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  convertArrayToString,
  getPayroll,
  getProcessCountry,
  getConcepts,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getClosedPayrolls,
  getBankId,
  getEmployees,
  ReportAtlantidaXmlHN,
  generateAtlantidaXmlHN
} from '@/app/core/services/reports/banks'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsBanks = ref<any[]>([])
const beforeBanks = ref<string[]>([])
const itemsEmployees = ref<string[]>([])
const copyItemsEmployees = ref<string[]>([])
const itemsProcesses = ref<string[]>([])
const itemsConcepts = ref<any[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<string[]>([])
const itemsAuthorizedIdType = ref([
  {
    id: 'employee_identification_number',
    name: `HN01 - ${t('reports.atlantidaXmlIdentification.fiscalId1')}`
  },
  {
    id: 'employee_fiscal_tax',
    name: `HN03 - ${t('reports.atlantidaXmlIdentification.fiscalId2')}`
  },
  {
    id: 'passport',
    name: `HN04 - ${t('reports.atlantidaXmlIdentification.passport')}`
  }
])
const itemsCompanyAccountType = ref([
  {
    id: 'CACC',
    name: `CACC - ${t('reports.atlantidaXmlAccountType.current')}`
  },
  { id: 'SVGS', name: `SVGS - ${t('reports.atlantidaXmlAccountType.savings')}` }
])
const searchProcesses = ref<string>('')
const searchConcept = ref<string>('')
const searchAuthorizedIdType = ref<string>('')
const searchCompanyAccountType = ref<string>('')
const urlFile = ref(null)
const refresh = ref()
const path = route.params.code as string
const decodeCode = atob(path)

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  process: string().required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  concepts: string().required(t('form.valid.required')),
  banks: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  messageId: string()
    .max(16, t('form.valid.maxLength', [16]))
    .required(t('form.valid.required')),
  clientCode: string()
    .max(35, t('form.valid.maxLength', [35]))
    .required(t('form.valid.required')),
  paymentOrderId: string()
    .max(35, t('form.valid.maxLength', [35]))
    .required(t('form.valid.required')),
  paymentDate: date()
    .min(
      dayjs().startOf('day').toDate(),
      `${t('form.valid.startDateMin')} ${dayjs().format('DD/MM/YYYY')}`
    )
    .required(t('form.valid.required')),  
  iban: string()
    .trim()
    .max(34, t('form.valid.maxLength', [34]))
    .when('accountNumberCompany', (accountNumberCompany: any, schema) =>
      `${accountNumberCompany[0] ?? ''}`.trim()
        ? schema.optional()
        : schema.required(t('form.valid.required'))
    ),
  accountNumberCompany: string()
    .trim()
    .max(34, t('form.valid.maxLength', [34]))
    .optional(),
  accountTypeCompany: string().optional(),
  paymentDescription: string()
    .max(140, t('form.valid.maxLength', [140]))
    .optional(),
  authorizedIdType: string().optional(),
  authorizedName: string()
    .max(140, t('form.valid.maxLength', [140]))
    .optional(),
  authorizedId: string()
    .max(35, t('form.valid.maxLength', [35]))
    .optional(),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') }))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: process } = useField<string>(ref('process'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: concepts } = useField<string>(ref('concepts'))
const { value: banks } = useField<string[]>(ref('banks'))
const { value: messageId } = useField<string>(ref('messageId'))
const { value: clientCode } = useField<string>(ref('clientCode'))
const { value: paymentOrderId } = useField<string>(ref('paymentOrderId'))
const { value: paymentDate } = useField<string>(ref('paymentDate'))
const { value: iban } = useField<string>(ref('iban'))
const { value: accountNumberCompany } = useField<string>(
  ref('accountNumberCompany')
)
const { value: accountTypeCompany } = useField<string>(
  ref('accountTypeCompany')
)
const { value: paymentDescription } = useField<string>(
  ref('paymentDescription')
)
const { value: includePhone } = useField<boolean>(ref('includePhone'))
const { value: includeEmail } = useField<boolean>(ref('includeEmail'))
const { value: authorizedIdType } = useField<string>(ref('authorizedIdType'))
const { value: authorizedName } = useField<string>(ref('authorizedName'))
const { value: authorizedId } = useField<string>(ref('authorizedId'))
const { value: employees } = useField<any[]>(ref('employees'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.concepts = allIdsConceptsSelected.value
  values.includePhone = !!values.includePhone
  values.includeEmail = !!values.includeEmail
  const createReport: ReportAtlantidaXmlHN = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateAtlantidaXmlHN(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar nóminas procesadas */
const getListProcessedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      payrolls.value?.length > 0 &&
      process.value != undefined &&
      startDate.value != undefined &&
      endDate.value != undefined
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strPayrolls = await convertArrayToString(payrolls.value)
      let _closedPayrollEmployees = await getClosedPayrolls(
        strPayrolls,
        `'${process.value}'`,
        startDate.value,
        endDate.value
      )
      let strEmployees = await convertArrayToString(_closedPayrollEmployees)
      getListEmployees(strEmployees)
      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Notificar que el banco del reporte no tiene bancos asociados en códigos bancarios */
const notifyNoBanks = () => {
  notifications.addNotification({
    title: t('reports.notification.noBanksFound'),
    text: t('reports.notification.noBanks'),
    type: 'warning',
    time: 10000
  })
}

/* Listar empleados */
const getListEmployees = async (employeesIds: string) => {
  if (!banks.value?.length) {
    notifyNoBanks()
    return
  }
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      let strBanks = await convertArrayToString(banks.value)
      itemsEmployees.value = await getEmployees(strBanks, employeesIds)
      copyItemsEmployees.value = itemsEmployees.value
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar conceptos de las nóminas y proceso seleccionados (por defecto el neto) */
const listConcepts = async () => {
  itemsConcepts.value = []
  allIdsConceptsSelected.value = []
  concepts.value = ''
  try {
    if (payrolls.value?.length > 0 && process.value != undefined) {
      loading.value = true
      messageLoading.value = t('loading.concepts')
      const strPayroll = await convertArrayToString(payrolls.value)
      allConcepts.value = await getConcepts(
        strPayroll,
        'lafise',
        '',
        `'${process.value}'`
      )
      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]
      const netConcept = itemsConcepts.value.find((_: any) =>
        _.code.includes('999999')
      )?.code
      if (netConcept) {
        concepts.value = netConcept
        filterCodeConcepts(netConcept)
      }
      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Ids del concepto seleccionado en todas las nóminas */
const filterCodeConcepts = (code: string) => {
  allIdsConceptsSelected.value = allConcepts.value
    .filter((item: any) => item.code === code)
    .map((_: any) => _.id)
}

/* Filtrar empleados por los bancos seleccionados */
const filterBanks = () => {
  let isEqual =
    beforeBanks.value.sort().toString() === banks.value.sort().toString()
  if (isEqual) return
  const employeesBanks = copyItemsEmployees.value.filter((employee: any) =>
    employee.bankId
      .split(', ')
      .some((bank: string) => banks.value.includes(bank))
  )
  employees.value = employeesBanks.map((_: any) => _.id)
  itemsEmployees.value = employeesBanks
  beforeBanks.value = banks.value
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseProcess, promiseBanks] = await Promise.all([
      getPayroll(store.user?.id as string),
      getProcessCountry(),
      getBankId(decodeCode)
    ])
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = promiseProcess
    itemsBanks.value = promiseBanks ?? []
    banks.value = itemsBanks.value.map((_: any) => _.id)
    if (itemsBanks.value.length === 0) notifyNoBanks()
    includePhone.value = false
    includeEmail.value = false
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.atlantidaXml')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="getListProcessedPayroll(), listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.process')"
                    v-model="process"
                    :required="true"
                    :options="filterOptions(itemsProcesses, searchProcesses)"
                    :error="errors.process"
                    @filter="searchProcesses = $event"
                    @change="getListProcessedPayroll(), listConcepts()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.concept')"
                    v-model="concepts"
                    :value-key="'code'"
                    :options="filterOptions(itemsConcepts, searchConcept)"
                    :required="true"
                    :error="errors.concepts"
                    @filter="searchConcept = $event"
                    @change="filterCodeConcepts(concepts)" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.banks')"
                    :required="true"
                    :header-title="t('reports.fields.banks')"
                    v-model="banks"
                    :options="itemsBanks"
                    :error="errors.banks"
                    @change="filterBanks" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="getListProcessedPayroll" />
                </div>

                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.tabs.fileData')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.messageId')"
                    v-model="messageId"
                    :required="true"
                    :maxlength="16"
                    :error="errors.messageId" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.clientCode')"
                    v-model="clientCode"
                    :required="true"
                    :maxlength="35"
                    :error="errors.clientCode" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.paymentOrderId')"
                    v-model="paymentOrderId"
                    :required="true"
                    :maxlength="35"
                    :error="errors.paymentOrderId" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.paymentDate')"
                    v-model="paymentDate"
                    :required="true"
                    :error="errors.paymentDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.companyAccountIBAN')"
                    v-model="iban"
                    :required="!accountNumberCompany?.trim()"
                    :maxlength="34"
                    :error="errors.iban" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.companyAccountNumber')"
                    v-model="accountNumberCompany"
                    :maxlength="34"
                    :error="errors.accountNumberCompany" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.companyAccountType')"
                    v-model="accountTypeCompany"
                    :options="
                      filterOptions(
                        itemsCompanyAccountType,
                        searchCompanyAccountType
                      )
                    "
                    @filter="searchCompanyAccountType = $event"
                    :error="errors.accountTypeCompany" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.paymentDescription')"
                    v-model="paymentDescription"
                    :maxlength="140"
                    :error="errors.paymentDescription" />
                </div>

                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.tabs.modifiers')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.authorizedIdType')"
                    v-model="authorizedIdType"
                    :options="
                      filterOptions(
                        itemsAuthorizedIdType,
                        searchAuthorizedIdType
                      )
                    "
                    :error="errors.authorizedIdType"
                    @filter="searchAuthorizedIdType = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DToggleField
                    :label="t('reports.fields.includePhone')"
                    v-model="includePhone" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DToggleField
                    :label="t('reports.fields.includeEmail')"
                    v-model="includeEmail" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.authorizedName')"
                    v-model="authorizedName"
                    :maxlength="140"
                    :error="errors.authorizedName" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.authorizedId')"
                    v-model="authorizedId"
                    :maxlength="35"
                    :error="errors.authorizedId" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.generateFile')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
